import { Field, TextInput, NumberInput, YesNoToggle, SectionHeader, TextArea } from "./Field";
import { Home, Users } from "lucide-react";

export default function StepPersonal({ form, update }) {
  return (
    <div>
      <SectionHeader
        eyebrow="Chapter I"
        title="Tell us about yourself."
        description="Start with the property you'd like to call home — then a few essentials."
      />

      {/* Property + Lease + Move-in */}
      <div className="mb-8 p-6 rounded-sm border border-[#A67C2E]/40 bg-[#A67C2E]/[0.06]">
        <div className="flex items-center gap-2 mb-5">
          <Home className="w-4 h-4 text-[#A67C2E]" />
          <p className="label-eyebrow text-[#A67C2E]">The Property</p>
        </div>
        <div className="space-y-5">
          <Field
            label="Address of the property you're applying for"
            hint="Include street, city, and unit number if applicable"
            required
            testid="field-property-applying"
          >
            <TextInput
              value={form.property_applying_for}
              onChange={(v) => update({ property_applying_for: v })}
              placeholder="123 Main Street, Unit 4B, Austin, TX 78704"
              testid="input-property-applying"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field
              label="Desired lease length"
              required
              testid="field-lease-length"
            >
              <select
                value={form.lease_length_months}
                onChange={(e) =>
                  update({ lease_length_months: Number(e.target.value) })
                }
                className="field-input"
                data-testid="input-lease-length"
              >
                <option value={6}>6 months</option>
                <option value={9}>9 months</option>
                <option value={12}>12 months</option>
              </select>
            </Field>

            <Field
              label="Preferred move-in date"
              required
              testid="field-move-in-date"
            >
              <TextInput
                type="date"
                value={form.preferred_move_in_date}
                onChange={(v) => update({ preferred_move_in_date: v })}
                testid="input-move-in-date"
              />
            </Field>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <Field label="Full Name" required testid="field-full-name">
          <TextInput
            value={form.full_name}
            onChange={(v) => update({ full_name: v })}
            placeholder="Jane A. Doe"
            testid="input-full-name"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Email" required testid="field-email">
            <TextInput
              type="email"
              value={form.email}
              onChange={(v) => update({ email: v })}
              placeholder="jane@example.com"
              testid="input-email"
            />
          </Field>
          <Field label="Phone" required testid="field-phone">
            <TextInput
              type="tel"
              value={form.phone}
              onChange={(v) => update({ phone: v })}
              placeholder="(555) 123-4567"
              testid="input-phone"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Date of Birth" required testid="field-dob">
            <TextInput
              type="date"
              value={form.date_of_birth}
              onChange={(v) => update({ date_of_birth: v })}
              testid="input-dob"
            />
          </Field>
          <Field
            label="Adults Residing"
            hint="Including yourself"
            required
            testid="field-adults"
          >
            <NumberInput
              value={form.number_of_adults}
              onChange={(v) => update({ number_of_adults: v })}
              placeholder="1"
              min={1}
              testid="input-adults"
            />
          </Field>
        </div>

        {/* Demographic notice */}
        <div
          className="rounded-sm border border-[#DDD1BC] bg-white p-5"
          data-testid="demographic-notice"
        >
          <div className="flex items-start gap-3">
            <Users className="w-4 h-4 text-[#A67C2E] shrink-0 mt-0.5" />
            <div>
              <p className="label-eyebrow text-[#A67C2E] mb-2">Please Note</p>
              <p className="text-sm text-[#4A423B] leading-relaxed">
                <strong>All tenants over 18 years old must sign the lease and pass the background check.</strong>{" "}
                Prior <em>non-violent</em> convictions do not automatically disqualify an applicant &mdash; each application is evaluated individually.
              </p>
            </div>
          </div>
        </div>

        <Field label="Do you have any pets?" testid="field-has-pets">
          <YesNoToggle
            value={form.has_pets}
            onChange={(v) =>
              update({ has_pets: v, pet_breeds: v ? form.pet_breeds : "" })
            }
            testid="toggle-has-pets"
          />
        </Field>

        {form.has_pets && (
          <Field
            label="If dogs, what breeds?"
            hint="List all dog breeds (e.g., Golden Retriever, Poodle)"
            testid="field-pet-breeds"
          >
            <TextArea
              value={form.pet_breeds}
              onChange={(v) => update({ pet_breeds: v })}
              placeholder="Golden Retriever, Labrador"
              rows={3}
              testid="input-pet-breeds"
            />
          </Field>
        )}
      </div>
    </div>
  );
}
